import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import {
    CreateSeriesPayload,
    CreateSeriesResponse,
    EditSeriesBookingPayload,
    SeriesBookingDetails,
    SeriesContinuation,
    SeriesRequestPayload,
} from '../../types/bookingCreation';
import { PreviewOccurrence, ServerSeriesPattern } from '../../utils/recurrence';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Dedicated slice for series-booking endpoints that live on the new
// (ECS) backend. Kept separate from `bookingApiSlice` because that slice
// targets the legacy Lambda backend via the old base URL.
//
// Cross-slice cache invalidation (`bookingList`, `bookingDetails`) is
// handled at the flow-hook call site by dispatching
// `bookingApiSlice.util.invalidateTags(...)` after a successful mutation.
export const seriesBookingApiSlice = createApi({
    reducerPath: 'seriesBookingApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['SeriesBooking'],
    endpoints: (builder) => ({
        getSeriesBooking: builder.query<SeriesBookingDetails, string>({
            query: (seriesId) => ({
                url: `/order/series/${seriesId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, seriesId) => [
                { type: 'SeriesBooking', id: seriesId },
            ],
        }),
        editSeriesBooking: builder.mutation<
            { success: boolean, includedBookingCount?: number, excludedBookingCount?: number },
            { seriesId: string, data: EditSeriesBookingPayload, saveMode: 'save_only' | 'save_and_send_email' }
        >({
            query: ({ seriesId, data, saveMode }) => {
                return {
                    url: `/order/series/${seriesId}?saveMode=${saveMode}`,
                    method: 'POST',
                    data: data,
                };
            },
            invalidatesTags: (_result, _error, { seriesId }) => [
                { type: 'SeriesBooking', id: seriesId },
            ],
        }),
        // Series module (series plan §4.2): the server expands the pattern and
        // asks the booking guard per date. `POST /admin/series/preview` —
        // `/admin` is in the base query, so the path starts at `series`.
        previewSeries: builder.mutation<{ occurrences: PreviewOccurrence[] }, SeriesRequestPayload>({
            query: (data) => ({ url: '/series/preview', method: 'POST', data }),
        }),
        // `POST /admin/series` — one booking per kept date, the payment term
        // and (with save_and_send_email) the one series mail. With `seriesId`
        // it adds dates to an existing series and leaves its term and mail
        // alone.
        createSeries: builder.mutation<
            CreateSeriesResponse,
            { data: CreateSeriesPayload, saveMode: 'save_only' | 'save_and_send_email' }
        >({
            query: ({ data, saveMode }) => ({ url: `/series?saveMode=${saveMode}`, method: 'POST', data }),
            invalidatesTags: (result) => (result?.seriesId ? [{ type: 'SeriesBooking', id: result.seriesId }] : []),
        }),
        // `POST /admin/series/:seriesId/continuation` — the next dates of the
        // series' stored rule as a pattern, previewed and created through the
        // two calls above. Writes nothing.
        continueSeries: builder.mutation<
            SeriesContinuation,
            { seriesId: string, endCondition: ServerSeriesPattern['endCondition'] }
        >({
            query: ({ seriesId, endCondition }) => ({
                url: `/series/${seriesId}/continuation`,
                method: 'POST',
                data: { endCondition },
            }),
        }),
    }),
});

export const {
    useGetSeriesBookingQuery,
    useEditSeriesBookingMutation,
    usePreviewSeriesMutation,
    useCreateSeriesMutation,
} = seriesBookingApiSlice;
